import { useInputForm } from 'components/dataCreationForms/withInputForm';
import React, { useCallback, useEffect, useState } from 'react';
import { isCompanyAddressStarted } from 'shared/domain/company/mapping';
import type { CompanyAddressesOnView } from 'shared/domain/company/types/view';
import { copiedAddressFields, isAddressCopied } from '../model';
import { CompanyAddressFieldsProps } from '../types';
import { CompanyAddressSectionPresentational } from './presentational';

/**
 * Copies another address into this one on request. The shared text field shows
 * only the value it mounted with, so once the form holds the copy, the section
 * remounts its fields to show it; the copied parts stay editable.
 */
export function CompanyAddressSection(
  props: CompanyAddressFieldsProps & { isDialog: boolean }
): React.ReactElement {
  const { kind, copySource } = props;
  const { values, setValues } = useInputForm();
  const fields = values as CompanyAddressesOnView;
  const [isCopyPending, setIsCopyPending] = useState(false);
  const [fieldsVersion, setFieldsVersion] = useState(0);

  const copyAddress = useCallback(() => {
    if (!copySource) {
      return;
    }
    copiedAddressFields(fields, copySource.kind, kind).forEach(
      ([key, value]) => setValues(key, value)
    );
    setIsCopyPending(true);
  }, [fields, copySource, kind, setValues]);

  useEffect(() => {
    if (
      isCopyPending &&
      copySource &&
      isAddressCopied(fields, copySource.kind, kind)
    ) {
      setIsCopyPending(false);
      setFieldsVersion((version) => version + 1);
    }
  }, [isCopyPending, fields, copySource, kind]);

  return (
    <CompanyAddressSectionPresentational
      {...props}
      fieldsVersion={fieldsVersion}
      canCopy={
        copySource
          ? isCompanyAddressStarted(copySource.kind, fields)
          : false
      }
      onCopy={copyAddress}
    />
  );
}
